import { useCallback, useRef, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  loading?: boolean;
  /** Server failure shown inside the dialog (and focused) so the user can retry or cancel. */
  error?: string | null;
  onConfirm: () => void | Promise<void>;
  /** Called once the dialog has fully closed and focus is back on its opener (after the exit animation). */
  onClosed?: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = false,
  loading = false,
  error = null,
  onConfirm,
  onClosed,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  // Stable callback ref: focuses the alert when it mounts, never on unrelated re-renders.
  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  const blockWhileLoading = (e: Event) => {
    if (loading) e.preventDefault();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        {...(description ? {} : { 'aria-describedby': undefined })}
        onOpenAutoFocus={(e) => {
          captureOpener();
          e.preventDefault();
          (destructive ? cancelRef.current : confirmRef.current)?.focus();
        }}
        onCloseAutoFocus={(e) => {
          restoreFocus(e);
          onClosed?.();
        }}
        onEscapeKeyDown={blockWhileLoading}
        onPointerDownOutside={blockWhileLoading}
        onInteractOutside={blockWhileLoading}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {error ? (
          <p ref={alertRef} tabIndex={-1} role="alert" className="text-sm text-destructive outline-none">
            {error}
          </p>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-0">
          {/* aria-disabled, not disabled: a disabled button drops focus to <body> mid-submit. */}
          <Button
            ref={cancelRef}
            type="button"
            variant="outline"
            aria-disabled={loading}
            onClick={() => {
              if (!loading) onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            aria-disabled={loading}
            aria-busy={loading}
            onClick={() => {
              if (loading) return;
              void onConfirm();
            }}
          >
            {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
